import { useCallback, useEffect } from 'react';
import { cache, type Canvas, type IText } from 'fabric';
import { useTranslation } from 'react-i18next';
import { useTextOperations } from '@/hooks/useTextOperations';
import {
  DEFAULT_DIRECT_SALE_FONT,
  ensureFontLoaded,
} from '@/config/direct-sale-fonts';
import { readableTextColor } from './panels/DirectSaleTextPanel';

const DEFAULT_SIZE = 30;

function isText(object: { type?: string } | null | undefined): object is IText {
  return (
    object?.type === 'i-text' ||
    object?.type === 'textbox' ||
    object?.type === 'text'
  );
}

/**
 * Typing straight onto the product, for the phone.
 *
 * The desktop creates text lazily from its panel's textarea. On a phone that
 * would mean a sheet over half the screen just to type, so the text object is
 * created at once, carrying a placeholder that is fully selected, and put
 * into Fabric's own editing mode — the customer types on the shirt and the
 * first keystroke replaces the placeholder.
 *
 * Everything up to `enterEditing()` stays synchronous inside the tap handler:
 * iOS only opens the keyboard for a focus that happens within the user
 * gesture, so the font is warmed up ahead of time rather than awaited here.
 *
 * A placeholder that was never replaced is not a design. It is removed when
 * editing ends, so merely opening the text tool never leaves billable
 * artwork behind — the same promise the desktop panel makes.
 */
export function useDirectSaleInlineText({
  canvas,
  enabled,
  productColor,
}: {
  canvas: Canvas | null;
  enabled: boolean;
  productColor?: string | null;
}) {
  const { t } = useTranslation();
  const { addText } = useTextOperations();
  const placeholder = t('directSale.design.textPlaceholder');
  const font = DEFAULT_DIRECT_SALE_FONT;

  // Warm the default face up, so the synchronous creation below measures
  // real glyphs instead of the fallback's.
  useEffect(() => {
    if (enabled) void ensureFontLoaded(font.fontFamily, font.fontWeight);
  }, [enabled, font]);

  // Editing ended: drop a text that says nothing of the customer's own.
  useEffect(() => {
    if (!canvas || !enabled) return;
    const onExit = (event: { target?: IText }) => {
      const target = event.target;
      if (!target || !isText(target)) return;
      const content = (target.text ?? '').trim();
      if (content !== '' && content !== placeholder) return;
      canvas.remove(target);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    };
    canvas.on('text:editing:exited', onExit);
    return () => {
      canvas.off('text:editing:exited', onExit);
    };
  }, [canvas, enabled, placeholder]);

  /** Put `text` into editing mode with everything selected. */
  const edit = useCallback(
    (text: IText, selectAll: boolean) => {
      if (!canvas) return;
      canvas.setActiveObject(text);
      text.enterEditing();
      if (selectAll) text.selectAll();
      // enterEditing focuses the hidden textarea itself; repeating it is
      // harmless and covers browsers that blurred it while re-rendering.
      text.hiddenTextarea?.focus();
      canvas.requestRenderAll();
    },
    [canvas]
  );

  /** The text tile: a new text, ready to be typed over. */
  const start = useCallback(() => {
    if (!canvas) return;
    // No FontFaceSet (very old browsers, jsdom): nothing to wait for.
    const loaded =
      document.fonts?.check(`${font.fontWeight} 16px "${font.fontFamily}"`) ??
      true;

    const created = addText({
      text: placeholder,
      fontFamily: font.fontFamily,
      fontWeight: font.fontWeight,
      fontSize: DEFAULT_SIZE,
      fill: readableTextColor(productColor),
      textAlign: 'center',
    });
    if (!created) return;
    edit(created, true);

    if (loaded) return;
    // The face arrived too late for the first measurement: Fabric cached
    // the fallback's glyph widths, so drop them and measure again.
    void ensureFontLoaded(font.fontFamily, font.fontWeight).then(() => {
      if (!canvas.getObjects().includes(created)) return;
      cache.clearFontCache(font.fontFamily);
      created.initDimensions();
      created.setCoords();
      canvas.requestRenderAll();
    });
  }, [addText, canvas, edit, font, placeholder, productColor]);

  /** The "write" pill: keep typing on the selected text. */
  const editSelected = useCallback(() => {
    const active = canvas?.getActiveObject();
    if (isText(active)) edit(active, false);
  }, [canvas, edit]);

  /** A tap on a text: carry on typing in it. */
  const editText = useCallback(
    (target: unknown) => {
      if (isText(target as { type?: string }) && !(target as IText).isEditing) {
        edit(target as IText, false);
      }
    },
    [edit]
  );

  return { start, editSelected, editText };
}
